import type { Node } from "@tiptap/pm/model";
import { Selection, TextSelection } from "@tiptap/pm/state";
export type SummaryDraftScope = { domain: string; userId: string; recordingId: string; meetingType: string };

export type SummaryEditorPosition = { anchor: number; head: number; scrollTop: number };
const storageKey = "notizy.summary-positions.v1";
const memory = new Map<string, SummaryEditorPosition>();
const scopeKey = (scope: SummaryDraftScope) => JSON.stringify([scope.domain, scope.userId, scope.recordingId, scope.meetingType]);
const valid = (value: unknown): value is SummaryEditorPosition => {
  const item = value as SummaryEditorPosition | null;
  return !!item && Number.isInteger(item.anchor) && item.anchor >= 0 && Number.isInteger(item.head) && item.head >= 0 &&
    Number.isFinite(item.scrollTop) && item.scrollTop >= 0;
};
function entries(): [string, SummaryEditorPosition][] {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(storageKey) ?? "[]");
    return Array.isArray(saved) ? saved.filter(item => Array.isArray(item) && typeof item[0] === "string" && valid(item[1])).slice(-100) : [];
  } catch { return []; }
}
export function readSummaryEditorPosition(scope: SummaryDraftScope): SummaryEditorPosition | undefined {
  if (!scope.userId) return;
  const key = scopeKey(scope);
  return memory.get(key) ?? entries().find(([id]) => id === key)?.[1];
}
export function writeSummaryEditorPosition(scope: SummaryDraftScope, position: SummaryEditorPosition) {
  if (!scope.userId || !valid(position)) return;
  const key = scopeKey(scope);
  memory.delete(key); memory.set(key, { ...position });
  if (memory.size > 100) memory.delete(memory.keys().next().value!);
  // Only coordinates are stored here; no report text is stored in browser preferences.
  try { localStorage.setItem(storageKey, JSON.stringify([...entries().filter(([id]) => id !== key), [key, position]].slice(-100))); }
  catch { /* Position persistence is optional; retain it for this app session. */ }
}
export function summaryPositionSelection(doc: Node, position: Pick<SummaryEditorPosition, "anchor" | "head">) {
  const clamp = (value: number) => Math.max(0, Math.min(value, doc.content.size));
  const anchor = Selection.near(doc.resolve(clamp(position.anchor)));
  const head = Selection.near(doc.resolve(clamp(position.head)));
  return anchor instanceof TextSelection && head instanceof TextSelection
    ? TextSelection.create(doc, anchor.anchor, head.head) : anchor;
}
